import React, { useRef } from 'react'
import {
  Checkbox,
  Col,
  Form,
  List,
  Modal,
  Radio,
  Row,
  Tooltip,
  Typography
} from 'antd'
import { Formik, FormikProps } from 'formik'
import { useTranslation } from 'react-i18next'

import { Button } from 'src/components/Ant'
import {
  currencyMask,
  Input,
  InputCurrency,
  SelectOperator,
  SelectResource
} from 'src/components/Formik'
import { Operator } from 'src/components/Formik/select-operator'
import { Policy, PolicyAction, PolicyReceiptRule } from 'src/types'

import * as CSS from './styles'
import * as Values from './initial-values'
import validationSchema from './validation-schema'

import { Empty, PolicyRuleAlertIcon } from '../../utils'

interface Props {
  formikProps: FormikProps<Policy>
  policyRuleIndex: number
}

function RecordText({ text = '' }: { text: string }): JSX.Element {
  if (text?.length > 20) {
    return (
      <Tooltip title={text}>
        <Typography.Text>{`${text.substring(0, 17)}...`}</Typography.Text>
      </Tooltip>
    )
  }
  return <Typography.Text>{text}</Typography.Text>
}

export default function ReceiptRules({ formikProps, policyRuleIndex }: Props) {
  const refFormik = useRef(null)

  const thisPolicy = formikProps.values?.policy_rules?.[policyRuleIndex]
  const { policy_id } = formikProps.values
  const { t } = useTranslation()

  function add() {
    refFormik.current?.resetForm(
      Values.getDefaultPolicyReceiptRulesValues({ policy_id })
    )
  }

  function handleSubmit(values: PolicyReceiptRule) {
    const index = formikProps.values.policy_rules[
      policyRuleIndex
    ].policy_receipt_rules?.findIndex(
      poReRu => poReRu.policy_receipt_rule_id === values.policy_receipt_rule_id
    )

    if (index > -1) {
      const policy_receipt_rules =
        formikProps.values.policy_rules[policyRuleIndex].policy_receipt_rules
      policy_receipt_rules[index] = values
      formikProps.setFieldValue(
        `policy_rules[${policyRuleIndex}].policy_receipt_rules`,
        policy_receipt_rules
      )
    } else {
      //is new
      formikProps.setFieldValue(
        `policy_rules[${policyRuleIndex}].policy_receipt_rules`,
        [
          ...(formikProps.values.policy_rules[policyRuleIndex]
            .policy_receipt_rules || []),
          values
        ]
      )
    }

    refFormik.current?.resetForm({})
  }

  const tAlert = t('web.policies.action.alert')
  const tBlock = t('web.policies.action.block')
  const tModalTitle = t('web.policies.rules.receipt.title')

  return (
    <>
      <CSS.Title level={4}>{tModalTitle}</CSS.Title>
      <CSS.Container>
        <CSS.List
          bordered
          dataSource={thisPolicy.policy_receipt_rules}
          locale={{
            emptyText: <Empty message={t('common.no-data-found')} />
          }}
          header={
            <Row>
              <Col span={2}>
                <Typography.Text strong>
                  {t('web.user.common.code.title')}
                </Typography.Text>
              </Col>
              <Col span={7}>
                <Typography.Text strong>
                  {t('web.user.common.first_name.title')}
                </Typography.Text>
              </Col>
              <CSS.Col span={3}>
                <Typography.Text strong>
                  {t('web.policies.rules.receipt.any-value')}
                </Typography.Text>
              </CSS.Col>
              <CSS.Col span={6}>
                <Typography.Text strong>
                  {t('common.condition.title')}
                </Typography.Text>
              </CSS.Col>
              <CSS.Col span={4}>
                <Typography.Text strong>
                  {t('web.policies.rules.receipt.limit-value')}
                </Typography.Text>
              </CSS.Col>
              <CSS.Col span={1}>
                <Typography.Text strong>
                  {t('web.policies.action.title')}
                </Typography.Text>
              </CSS.Col>
              <CSS.Col span={1}>
                <Typography.Text strong>{t('common.active')}</Typography.Text>
              </CSS.Col>
            </Row>
          }
          renderItem={(receiptRule, itemIndex) => {
            return (
              <List.Item
                key={receiptRule.policy_receipt_rule_id}
                onClick={() => {
                  refFormik.current?.resetForm(receiptRule)
                }}
              >
                <Col span={2}>
                  <RecordText text={receiptRule.policy_receipt_rule_prefix} />
                </Col>
                <Col span={7}>
                  <RecordText text={receiptRule.description} />
                </Col>
                <CSS.Col span={3}>
                  <Checkbox checked={receiptRule?.any_value} disabled />
                </CSS.Col>
                <CSS.Col span={6}>
                  <Operator type={receiptRule.operator} />
                </CSS.Col>
                <CSS.Col span={4}>
                  {
                    currencyMask({
                      formatStr: true,
                      precision: 2,
                      value: receiptRule.value
                    }).maskedValue
                  }
                </CSS.Col>
                <CSS.Col span={1}>
                  <PolicyRuleAlertIcon
                    action={receiptRule.action}
                    alert={tAlert}
                    block={tBlock}
                  />
                </CSS.Col>
                <CSS.Col span={1} onClick={event => event.stopPropagation()}>
                  <Checkbox
                    defaultChecked={Boolean(receiptRule?.active)}
                    onChange={value => {
                      const { checked } = value.target
                      formikProps.setFieldValue(
                        `policy_receipt_rule[${itemIndex}].active`,
                        checked
                      )
                    }}
                  />
                </CSS.Col>
              </List.Item>
            )
          }}
        />
      </CSS.Container>
      <CSS.ContainerButtonAdd>
        <Button
          icon="plus"
          key="common.actions.add"
          onClick={add}
          size="large"
          type="primary"
        >
          {t('common.actions.add')}
        </Button>
      </CSS.ContainerButtonAdd>
      <Formik
        initialValues={Values.initialValues}
        onSubmit={handleSubmit}
        ref={refFormik}
        render={receiptRuleForm => {
          const visible = Boolean(
            receiptRuleForm?.values?.policy_receipt_rule_id
          )

          return (
            <Modal
              centered
              closable
              destroyOnClose
              mask
              maskClosable
              okText={t('common.actions.save')}
              cancelText={t('common.actions.close')}
              onCancel={() => {
                receiptRuleForm.resetForm(Values.initialValues)
              }}
              onOk={() => {
                receiptRuleForm.handleSubmit()
              }}
              title={tModalTitle}
              visible={visible}
              width={800}
            >
              <Row gutter={24}>
                <Col span={12}>
                  <Input
                    formikProps={receiptRuleForm}
                    label="web.policies.rules.prefix"
                    name="policy_receipt_rule_prefix"
                    placeholder="web.policies.rules.prefix-placeholder"
                  />
                </Col>
                <Col span={12}>
                  <Input
                    formikProps={receiptRuleForm}
                    label="web.policies.rules.description"
                    name="description"
                    placeholder="web.policies.rules.description-placeholder"
                  />
                </Col>
              </Row>
              <Row gutter={24}>
                <Col span={12}>
                  <SelectResource
                    formikProps={receiptRuleForm}
                    keyLabel="name"
                    keyValue="service_id"
                    label="web.receipt.form.service.title"
                    name="service_id"
                    resource="services"
                    style={{ width: '100%' }}
                  />
                </Col>
                <Col span={12}>
                  <SelectResource
                    formikProps={receiptRuleForm}
                    keyLabel="name"
                    keyValue="reason_id"
                    label="web.resources.reasons.title"
                    name="reason_id"
                    resource="reasons"
                    style={{ width: '100%' }}
                  />
                </Col>
              </Row>
              <Row gutter={24}>
                <Col span={12}>
                  <SelectResource
                    formikProps={receiptRuleForm}
                    keyLabel="name"
                    keyValue="reference_id"
                    label="web.resources.references.title"
                    name="reference_id"
                    resource="references"
                    style={{ width: '100%' }}
                  />
                </Col>
                <Col span={12}>
                  <SelectResource
                    formikProps={receiptRuleForm}
                    keyLabel="code"
                    keyValue="currency_id"
                    label="web.resources.currencies.title"
                    name="currency_id"
                    resource="currencies"
                    style={{ width: '100%' }}
                  />
                </Col>
              </Row>

              <Row gutter={[0, 12]}>
                <Col span={24}>
                  <div
                    onClick={() => {
                      receiptRuleForm.setFieldValue(
                        'any_value',
                        !receiptRuleForm.values.any_value
                      )
                    }}
                  >
                    <div>
                      <CSS.SubTitle strong>
                        {t('web.policies.rules.receipt.any-value-modal')}
                      </CSS.SubTitle>
                    </div>
                    <CSS.SwitchDiv>
                      <CSS.Switch
                        checked={receiptRuleForm.values.any_value}
                        checkedChildren={t('common.active')}
                        unCheckedChildren={t('common.inactive')}
                      />
                      <CSS.TextInfo>
                        {t('web.policies.rules.receipt.any-value-modal-info')}
                      </CSS.TextInfo>
                    </CSS.SwitchDiv>
                  </div>
                </Col>
              </Row>
              <Row gutter={[0, 12]}>
                <Col span={24}>
                  <div>
                    <CSS.SubTitle strong>
                      {t('web.policies.rules.receipt.limit-value-modal')}
                    </CSS.SubTitle>
                  </div>
                  <div>
                    <CSS.TextInfo>
                      {t('web.policies.rules.receipt.limit-value-modal-info')}
                    </CSS.TextInfo>
                  </div>
                </Col>
              </Row>
              <Row gutter={24}>
                <Col span={6}>
                  <SelectOperator
                    disabled={receiptRuleForm.values?.any_value}
                    formikProps={receiptRuleForm}
                  />
                </Col>
                <Col span={6}>
                  <InputCurrency
                    dataTestId="policy-daily-limit"
                    disabled={receiptRuleForm.values?.any_value}
                    formikProps={receiptRuleForm}
                    label="web.policies.limits.daily"
                    name="value"
                  />
                </Col>
              </Row>
              <Row>
                <Col span={6}>
                  <Form.Item
                    colon={false}
                    label={t('web.policies.action.title')}
                  >
                    <Radio.Group
                      buttonStyle="solid"
                      onChange={e => {
                        let policyAction: PolicyAction = PolicyAction.ALERT

                        if (e.target.value === 'BLOCK') {
                          policyAction = PolicyAction.BLOCK
                        }

                        receiptRuleForm.setFieldValue('action', policyAction)
                      }}
                      size="small"
                      value={receiptRuleForm.values?.action}
                    >
                      <Radio.Button value="ALERT">{tAlert}</Radio.Button>
                      <Radio.Button value="BLOCK">{tBlock}</Radio.Button>
                    </Radio.Group>
                  </Form.Item>
                </Col>
              </Row>
            </Modal>
          )
        }}
        validationSchema={validationSchema}
      />
    </>
  )
}
